import { useMemo } from 'react';
import { getFieldLabel } from '../../utils/scan';
import { scanCategoryData } from '../scan/ScannerCategory';

// Column types drive cell rendering and sizing
export const COLUMN_TYPE = {
  SYMBOL: 'symbol',
  PRICE: 'price',
  CHANGE: 'change',
  TEXT: 'text',
  DEFAULT: 'default'
};

// Known field → column config map.
// Fields listed here get special rendering / widths; all others fall back to COLUMN_TYPE.DEFAULT.
export const KNOWN_COLUMN_CONFIG = {
  price: { type: COLUMN_TYPE.PRICE, width: 120 },
  close: { type: COLUMN_TYPE.PRICE, width: 120 },
  currentPrice: { type: COLUMN_TYPE.PRICE, width: 120 },
  change: { type: COLUMN_TYPE.CHANGE, width: 64 },
  netChange: { type: COLUMN_TYPE.CHANGE, width: 64 },
  securityType: { type: COLUMN_TYPE.TEXT },
  exchange: { type: COLUMN_TYPE.TEXT }
};

// Map field keys to localised header labels
export const getHeaderName = (field, localize) => {
  const columnOverrides = {
    symbol: localize('scan.label.symbol') || '銘柄',
    price: localize('scan.column.price') || '現在値（円）',
    close: localize('scan.column.price') || '現在値（円）',
    currentPrice: localize('scan.column.price') || '現在値（円）',
    change: localize('scan.column.change') || '前日比',
    netChange: localize('scan.column.change') || '前日比',
    changePercent: localize('scan.column.changePercent') || '前日比（%）'
  };
  if (columnOverrides[field]) return columnOverrides[field];

  const fieldLower = field.toLowerCase();
  const canonicalId = scanCategoryData[field]
    ? field
    : Object.keys(scanCategoryData).find(
        (key) => key.toLowerCase() === fieldLower
      ) || field;

  return getFieldLabel(canonicalId, localize);
};

/**
 * Hook that derives column descriptors, the fixed symbol column,
 * scrollable columns, and the CSS grid template from scan results.
 */
export const useScanColumns = (scanResults, localize) => {
  const columns = useMemo(() => {
    if (!scanResults || scanResults.length === 0) return [];

    const firstResult = scanResults[0];
    const cols = [];

    if ('symbol' in firstResult) {
      cols.push({
        key: 'symbol',
        header: getHeaderName('symbol', localize),
        type: COLUMN_TYPE.SYMBOL,
        width: null
      });
    }

    Object.keys(firstResult).forEach((key) => {
      if (key === 'symbol') return;
      const known = KNOWN_COLUMN_CONFIG[key];
      cols.push({
        key,
        header: getHeaderName(key, localize),
        type: known ? known.type : COLUMN_TYPE.DEFAULT,
        width: known ? known.width : null
      });
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanResults]);

  const symbolColumn =
    columns.length > 0 && columns[0].type === COLUMN_TYPE.SYMBOL
      ? columns[0]
      : null;

  const scrollableColumns = useMemo(
    () => columns.slice(symbolColumn ? 1 : 0),
    [columns, symbolColumn]
  );

  const scrollableGridTemplate = useMemo(() => {
    if (scrollableColumns.length === 0) {
      return 'none';
    }

    return scrollableColumns
      .map((column) => {
        if (column.width) {
          return `${column.width}px`;
        }
        if (column.type === COLUMN_TYPE.TEXT) {
          return '160px';
        }
        return '120px';
      })
      .join(' ');
  }, [scrollableColumns]);

  return { columns, symbolColumn, scrollableColumns, scrollableGridTemplate };
};
